import { hasPlatformPermission, PLATFORM_PERMISSIONS, type GlobalRole } from '@napaicp/shared'

/**
 * UX-009: the "平台管理" nav entry, and the `/platform/*` route family
 * itself, must only be reachable/visible for an account holding at least one
 * `platform.*.read` permission — never derived from a hard-coded
 * `globalRole === 'super_admin'` string check (GOV-013, mirrored client-side
 * here the same way every `/api/platform/*` route already does it
 * server-side via `requirePlatformPermission`). An account with no platform
 * read permission must see nothing that hints platform governance exists.
 *
 * This is advisory only — the server is still the authority on every actual
 * request (UX-002) — but it is what decides whether the nav link and the
 * `/platform/*` shell render at all.
 */
export function hasAnyPlatformReadAccess(globalRole: GlobalRole): boolean {
  return (
    hasPlatformPermission({ globalRole }, PLATFORM_PERMISSIONS.DASHBOARD_READ) ||
    hasPlatformPermission({ globalRole }, PLATFORM_PERMISSIONS.USERS_READ) ||
    hasPlatformPermission({ globalRole }, PLATFORM_PERMISSIONS.MEMBERSHIPS_READ) ||
    hasPlatformPermission({ globalRole }, PLATFORM_PERMISSIONS.JUDGES_READ) ||
    hasPlatformPermission({ globalRole }, PLATFORM_PERMISSIONS.ADMINS_READ) ||
    hasPlatformPermission({ globalRole }, PLATFORM_PERMISSIONS.AUDIT_READ) ||
    hasPlatformPermission({ globalRole }, PLATFORM_PERMISSIONS.OPERATIONS_READ) ||
    // Wave O/GOV-019/020: a caller who can only review Competition Creation
    // Requests still needs the `/platform` shell to reach that queue — today
    // every platform permission is only ever held by `super_admin` (see
    // `packages/shared/src/permissions.ts`), so this is a no-op in practice,
    // but keeps the gate correct if a narrower reviewer role is ever added.
    hasPlatformPermission({ globalRole }, PLATFORM_PERMISSIONS.COMPETITIONS_APPROVE) ||
    // PR-130: same reasoning as COMPETITIONS_APPROVE above — a caller who can
    // only manage the Discovery Category taxonomy still needs the shell.
    hasPlatformPermission({ globalRole }, PLATFORM_PERMISSIONS.DISCOVERY_MANAGE)
  )
}

export function canReadPlatform(
  globalRole: GlobalRole,
  permission: (typeof PLATFORM_PERMISSIONS)[keyof typeof PLATFORM_PERMISSIONS],
): boolean {
  return hasPlatformPermission({ globalRole }, permission)
}
